import { Actions, Subjects } from '@/client/users';
import { ViewOnlyRestrictionWhen } from '@/components/custom-roles';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { FlexContainer } from '@/ui';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AddButton } from './AddButton';

const StyledFlexContainer = styled(FlexContainer)`
  border-radius: var(--semi-medium-border-radius);
  background: var(--gray-soft);
  padding: var(--semi-medium-padding);
  padding-left: 32px;
  margin-top: var(--default-padding);
  overflow-x: auto;
`;

const AddSimulationsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
`;

const AddSimulationsTitle = styled.h3`
  color: var(--black-main);
  font-size: var(--semi-big-font-size);
  font-weight: 700;
  margin: 0;
`;

const AddSimulationsDescription = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  margin: 0;
  max-width: 350px;
  line-height: 1.4;
`;

const SimulationOptions = styled.div`
  display: flex;
  gap: var(--small-padding);
  min-width: 650px;
`;

type AddSimulationsCardProps = {
  disabledPlanWithAssistance?: boolean;
  hidePlanWithAssistance?: boolean;
  onSingleSimulation?: () => void;
  onPlanWithAssistance?: () => void;
};

export const AddSimulationsCard: React.FC<AddSimulationsCardProps> = ({
  disabledPlanWithAssistance,
  hidePlanWithAssistance,
  onSingleSimulation,
  onPlanWithAssistance,
}) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const { isRestrictedByRole } = usePermission();
  const isAddRestricted = isRestrictedByRole(
    Actions.CREATE,
    Subjects.CAMPAIGNS,
  );

  return (
    <StyledFlexContainer justify="space-between">
      <AddSimulationsContent>
        <AddSimulationsTitle>
          {t('phishing.simulations.addCard.title')}
        </AddSimulationsTitle>
        <AddSimulationsDescription>
          {t('phishing.simulations.addCard.description')}
        </AddSimulationsDescription>
      </AddSimulationsContent>
      <SimulationOptions>
        <ViewOnlyRestrictionWhen restricted={isAddRestricted}>
          <AddButton
            title={t('phishing.simulations.addCard.singleOption.title')}
            description={t(
              'phishing.simulations.addCard.singleOption.description',
            )}
            id="single-simulation"
            disabled={isAddRestricted}
            onClick={isAddRestricted ? undefined : onSingleSimulation}
          />
        </ViewOnlyRestrictionWhen>
        {currentUser?.account?.isSystem && (
          <div
            style={{
              display: hidePlanWithAssistance ? 'none' : 'contents',
            }}
          >
            <AddButton
              title={t('phishing.simulations.addCard.assistanceOption.title')}
              description={t(
                'phishing.simulations.addCard.assistanceOption.description',
              )}
              id="plan-with-assistance"
              disabled={disabledPlanWithAssistance}
              onClick={onPlanWithAssistance}
            />
            {!!disabledPlanWithAssistance && (
              <Tooltip
                target="#plan-with-assistance"
                content={t(
                  'phishing.simulations.addCard.assistanceOption.disabled.tooltip',
                )}
                position="top"
                showOnDisabled
              />
            )}
          </div>
        )}
      </SimulationOptions>
    </StyledFlexContainer>
  );
};
